import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { CalendarDays } from 'lucide-react'
import { cn } from '@lib/utils'
import { useClinicFormat } from '@context/ClinicTimezoneContext'
import type { Appointment } from '@/types'
import { doctorLabel, patientLabel } from '@hooks/useScarlettInbox'

const STATUS: Record<string, { label: string; dot: string; chip: string }> = {
  pending_approval: { label: 'Por confirmar', dot: 'bg-amber-400', chip: 'bg-amber-50 text-amber-800 border-amber-200' },
  scheduled:        { label: 'Programada',    dot: 'bg-ml-primary', chip: 'bg-ml-primary/10 text-ml-primary border-ml-primary/15' },
  confirmed:        { label: 'Confirmada',    dot: 'bg-ml-primary', chip: 'bg-ml-primary/10 text-ml-primary border-ml-primary/15' },
  checked_in:       { label: 'En sala',       dot: 'bg-sky-500',    chip: 'bg-sky-50 text-sky-800 border-sky-200' },
  in_progress:      { label: 'En consulta',   dot: 'bg-emerald-500', chip: 'bg-emerald-50 text-emerald-800 border-emerald-200' },
  completed:        { label: 'Atendida',      dot: 'bg-emerald-600', chip: 'bg-emerald-50 text-emerald-800 border-emerald-200' },
  no_show:          { label: 'No asistió',    dot: 'bg-slate-400',  chip: 'bg-slate-100 text-slate-600 border-slate-200' },
}
const HIDDEN = new Set(['cancelled', 'rejected'])

interface TodayTimelineProps {
  appointments: Appointment[]
  isLoading: boolean
}

export function TodayTimeline({ appointments, isLoading }: TodayTimelineProps) {
  const navigate = useNavigate()
  const { formatTime } = useClinicFormat()
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 60_000)
    return () => clearInterval(id)
  }, [])

  const items = useMemo(
    () => appointments
      .filter((a) => !HIDDEN.has(a.status))
      .sort((a, b) => a.appointment_date.localeCompare(b.appointment_date)),
    [appointments],
  )
  const nextIdx = items.findIndex((a) => new Date(a.appointment_date).getTime() + (a.duration_minutes || 30) * 60_000 > now)

  return (
    <section className="flex min-w-0 flex-col rounded-2xl border border-ml-outline-variant/15 bg-ml-surface-container-lowest">
      <header className="flex flex-wrap items-baseline justify-between gap-2 border-b border-ml-outline-variant/10 px-5 py-4 sm:px-6">
        <div>
          <h2 className="font-headline text-2xl text-ml-primary">Agenda de hoy</h2>
          <p className="text-xs text-ml-on-surface-variant/70">{items.length ? `${items.length} ${items.length === 1 ? 'cita' : 'citas'} · orden cronológico` : 'Nada agendado todavía'}</p>
        </div>
        <button onClick={() => navigate('/agenda')} className="text-sm font-semibold text-ml-primary underline-offset-4 hover:underline">Ver calendario →</button>
      </header>

      {isLoading ? (
        <div className="space-y-3 p-5">
          {[0, 1, 2].map((i) => <div key={i} className="h-16 animate-pulse rounded-xl bg-ml-surface-container-low" />)}
        </div>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center gap-3 px-6 py-14 text-center">
          <span className="grid h-12 w-12 place-items-center rounded-2xl bg-ml-surface-container-low text-ml-primary"><CalendarDays className="h-6 w-6" /></span>
          <p className="font-headline text-xl text-ml-primary">Día despejado</p>
          <p className="max-w-xs text-sm text-ml-on-surface-variant">Las citas que Scarlett agende por WhatsApp aparecerán aquí en cuanto las confirmes.</p>
        </div>
      ) : (
        <ol className="relative px-3 py-2 sm:px-4">
          {items.map((a, i) => {
            const st = STATUS[a.status] ?? { label: a.status, dot: 'bg-slate-400', chip: 'bg-slate-100 text-slate-600 border-slate-200' }
            const start = new Date(a.appointment_date).getTime()
            const past = start + (a.duration_minutes || 30) * 60_000 <= now
            const isNext = i === nextIdx
            const showNowLine = i === nextIdx && i > 0
            return (
              <li key={a.id} className="relative">
                {showNowLine && (
                  <div className="my-1 flex items-center gap-2 px-2" aria-label="Ahora">
                    <span className="h-2 w-2 rounded-full bg-ml-error motion-safe:animate-pulse" />
                    <span className="h-px flex-1 bg-ml-error/50" />
                    <span className="text-[10px] font-bold uppercase tracking-widest text-ml-error">ahora</span>
                  </div>
                )}
                <button
                  onClick={() => navigate('/agenda')}
                  className={cn(
                    'grid w-full grid-cols-[5.5rem_1fr] items-center gap-3 rounded-xl px-2 py-3 text-left transition sm:grid-cols-[7rem_1fr_auto] sm:gap-4 sm:px-3',
                    isNext ? 'bg-ml-primary/[0.06] ring-1 ring-ml-primary/15' : 'hover:bg-ml-surface-container-low/70',
                    past && !isNext && 'opacity-60',
                  )}
                >
                  <div className="flex flex-col">
                    <span className={cn('whitespace-nowrap font-headline text-xl leading-none tabular-nums sm:text-2xl', isNext ? 'text-ml-primary' : 'text-ml-on-surface')}>{formatTime(a.appointment_date)}</span>
                    <span className="mt-1 text-[11px] text-ml-on-surface-variant/70">{a.duration_minutes || 30} min</span>
                  </div>
                  <div className="min-w-0">
                    <p className="flex items-center gap-2 truncate text-sm font-semibold text-ml-on-surface">
                      <span className={cn('h-2 w-2 shrink-0 rounded-full', st.dot)} />
                      <span className="truncate">{patientLabel(a)}</span>
                    </p>
                    <p className="truncate text-xs text-ml-on-surface-variant/80">
                      {a.reason || a.appointment_type}{doctorLabel(a) ? ` · ${doctorLabel(a)}` : ''}
                    </p>
                    <span className={cn('mt-1 inline-flex rounded-full border px-2 py-0.5 text-[10px] font-semibold sm:hidden', st.chip)}>{st.label}</span>
                  </div>
                  <span className={cn('hidden rounded-full border px-2.5 py-1 text-[11px] font-semibold sm:inline-flex', st.chip)}>{st.label}</span>
                </button>
              </li>
            )
          })}
        </ol>
      )}
    </section>
  )
}
